import moment from 'moment';
import { BusinessHours, HourMarker, PIXELS_PER_HOUR, TRACK_INSET } from './dayViewLayout';
import { toAmsterdam, amsterdamDateTime, formatAmsterdam } from '../utils/time';

export { PIXELS_PER_HOUR, TRACK_INSET };
export type { BusinessHours, HourMarker };

/** Het minimum dat de agenda nodig heeft om een blok te kunnen plaatsen. */
export interface TimedItem {
  id: number;
  start_at: string;
  end_at: string;
}

export interface WeekBlock<T extends TimedItem> {
  key: string;
  item: T;
  startLabel: string;
  endLabel: string;
  topPercent: number;
  heightPercent: number;
  leftPercent: number;
  widthPercent: number;
  /** De reservering begint voor of eindigt na deze dag (loopt over middernacht). */
  continuesBefore: boolean;
  continuesAfter: boolean;
}

export interface WeekDay<T extends TimedItem> {
  key: string;
  date: string;
  weekdayLabel: string;
  dateLabel: string;
  isToday: boolean;
  blocks: WeekBlock<T>[];
}

export interface WeekLayout<T extends TimedItem> {
  days: WeekDay<T>[];
  hourMarkers: HourMarker[];
  trackHeight: number;
  rangeLabel: string;
  blockCount: number;
}

// Een blok van een kwartier zou anders een streepje worden waar het
// reserveringsnummer niet in past.
const MIN_BLOCK_MINUTES = 30;

const MINUTES_PER_DAY = 24 * 60;

/** Maandag als eerste dag, zoals in een papieren weekagenda. */
export const startOfWeek = (value: moment.MomentInput): moment.Moment =>
  moment(value).startOf('isoWeek');

const toMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

const floorToHour = (minutes: number): number => Math.floor(minutes / 60) * 60;

const ceilToHour = (minutes: number): number => Math.ceil(minutes / 60) * 60;

const pad = (value: number): string => (value < 10 ? `0${value}` : String(value));

const formatMinutes = (minutes: number): string => {
  const clamped = Math.min(minutes, MINUTES_PER_DAY);
  return `${pad(Math.floor(clamped / 60))}:${pad(clamped % 60)}`;
};

// Intl in plaats van een moment-locale, zodat er geen extra taalbestand nodig is.
const formatWeekday = (date: moment.Moment): string =>
  date.toDate().toLocaleDateString('nl-NL', { weekday: 'short' }).replace('.', '');

const formatDayAndMonth = (date: moment.Moment): string =>
  date.toDate().toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }).replace('.', '');

export const formatWeekRange = (weekStart: moment.Moment): string => {
  const weekEnd = weekStart.clone().add(6, 'days');
  const start = weekStart.toDate().toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: weekStart.month() === weekEnd.month() ? undefined : 'long'
  });
  const end = weekEnd.toDate().toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  return `${start} - ${end}`;
};

interface DaySegment<T extends TimedItem> {
  item: T;
  startMinutes: number;
  endMinutes: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
  column: number;
  columns: number;
}

/**
 * Verdeelt reserveringen die op dezelfde dag over elkaar heen lopen over
 * kolommetjes naast elkaar, zodat elke reservering zichtbaar blijft ook als
 * meerdere fietsen op hetzelfde moment verhuurd zijn.
 */
const assignColumns = <T extends TimedItem>(segments: DaySegment<T>[]): void => {
  let cluster: DaySegment<T>[] = [];
  let clusterEnd = -1;

  const closeCluster = (): void => {
    const columns = cluster.reduce((max, segment) => Math.max(max, segment.column + 1), 0);
    cluster.forEach(segment => {
      segment.columns = columns;
    });
    cluster = [];
    clusterEnd = -1;
  };

  segments.forEach(segment => {
    if (cluster.length > 0 && segment.startMinutes >= clusterEnd) {
      closeCluster();
    }

    // Eerste kolom pakken die op dit tijdstip weer vrij is.
    const takenColumns = cluster
      .filter(other => other.endMinutes > segment.startMinutes)
      .map(other => other.column);
    let column = 0;
    while (takenColumns.indexOf(column) !== -1) {
      column += 1;
    }

    segment.column = column;
    cluster.push(segment);
    clusterEnd = Math.max(clusterEnd, segment.endMinutes);
  });

  if (cluster.length > 0) {
    closeCluster();
  }
};

export const computeWeekLayout = <T extends TimedItem>(
  weekStart: moment.Moment,
  businessHours: BusinessHours,
  items: T[]
): WeekLayout<T> => {
  const days = Array.from({ length: 7 }, (_, index) => weekStart.clone().startOf('day').add(index, 'days'));
  const today = formatAmsterdam(new Date(), 'YYYY-MM-DD');

  const segmentsPerDay = days.map(day => {
    // Dag-grenzen in Amsterdamse tijd, zodat de minuten-op-de-dag kloppen los
    // van de tijdzone van de browser.
    const dayStart = amsterdamDateTime(day.format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day');
    const dayEnd = dayStart.clone().endOf('day');

    const segments: DaySegment<T>[] = items
      .map(item => ({ item, start: toAmsterdam(item.start_at), end: toAmsterdam(item.end_at) }))
      .filter(entry => entry.end.isAfter(dayStart) && entry.start.isBefore(dayEnd))
      .map(entry => {
        const continuesBefore = entry.start.isBefore(dayStart);
        const continuesAfter = entry.end.isAfter(dayEnd);
        return {
          item: entry.item,
          startMinutes: continuesBefore ? 0 : entry.start.diff(dayStart, 'minutes'),
          endMinutes: continuesAfter ? MINUTES_PER_DAY : entry.end.diff(dayStart, 'minutes'),
          continuesBefore,
          continuesAfter,
          column: 0,
          columns: 1
        };
      })
      .sort((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes);

    assignColumns(segments);
    return segments;
  });

  // Het venster volgt de openingstijden, maar rekt op zodra een reservering
  // erbuiten valt; anders zou die reservering onzichtbaar zijn.
  let windowStartMinutes = toMinutes(businessHours.start);
  let windowEndMinutes = toMinutes(businessHours.end);
  segmentsPerDay.forEach(segments => {
    segments.forEach(segment => {
      windowStartMinutes = Math.min(windowStartMinutes, segment.startMinutes);
      windowEndMinutes = Math.max(windowEndMinutes, segment.endMinutes);
    });
  });

  windowStartMinutes = Math.max(floorToHour(windowStartMinutes), 0);
  windowEndMinutes = Math.min(ceilToHour(windowEndMinutes), MINUTES_PER_DAY);
  const windowMinutes = Math.max(windowEndMinutes - windowStartMinutes, 60);

  const toPercent = (minutes: number): number =>
    ((minutes - windowStartMinutes) / windowMinutes) * 100;

  const minHeightPercent = (MIN_BLOCK_MINUTES / windowMinutes) * 100;

  const hourMarkers: HourMarker[] = [];
  for (let minutes = windowStartMinutes; minutes <= windowEndMinutes; minutes += 60) {
    hourMarkers.push({
      key: `hour-${minutes}`,
      label: formatMinutes(minutes),
      topPercent: toPercent(minutes)
    });
  }

  const layoutDays: WeekDay<T>[] = days.map((day, index) => {
    const date = day.format('YYYY-MM-DD');
    const blocks = segmentsPerDay[index].map(segment => {
      const topPercent = Math.min(Math.max(toPercent(segment.startMinutes), 0), 100);
      const rawHeight = Math.min(toPercent(segment.endMinutes), 100) - topPercent;
      const width = 100 / segment.columns;
      return {
        key: `reservation-${segment.item.id}-${date}`,
        item: segment.item,
        startLabel: formatMinutes(segment.startMinutes),
        endLabel: formatMinutes(segment.endMinutes),
        topPercent,
        heightPercent: Math.max(rawHeight, minHeightPercent),
        leftPercent: segment.column * width,
        widthPercent: width,
        continuesBefore: segment.continuesBefore,
        continuesAfter: segment.continuesAfter
      };
    });

    return {
      key: date,
      date,
      weekdayLabel: formatWeekday(day),
      dateLabel: formatDayAndMonth(day),
      isToday: date === today,
      blocks
    };
  });

  return {
    days: layoutDays,
    hourMarkers,
    trackHeight: (windowMinutes / 60) * PIXELS_PER_HOUR + 2 * TRACK_INSET,
    rangeLabel: formatWeekRange(weekStart),
    blockCount: layoutDays.reduce((total, day) => total + day.blocks.length, 0)
  };
};
